import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useLiveRefresh } from "../hooks/useLiveRefresh";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { todayISO, ROOM_STATUSES } from "../lib/format";
import { useI18n } from "../lib/i18n";
import { roomTypeFor, ROOM_TYPES } from "../lib/roomLinen";
import { useAuth, hasRole } from "../lib/auth";

const ACTIONS_KEYS = [
  { value: "place_in_room", key: "hk.act.place" },
  { value: "remove_from_room", key: "hk.act.remove" },
  { value: "found_dirty", key: "hk.act.dirty" },
  { value: "report_missing", key: "hk.act.missing" },
  { value: "report_damage", key: "hk.act.damage" },
  { value: "physical_count", label: "Physical Count / Verify" },
];

export default function Housekeeping() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = hasRole(user, "admin");
  const [assignType, setAssignType] = useState("");
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState("");
  const [opDate, setOpDate] = useState(todayISO());
  const [roomStatus, setRoomStatus] = useState("");
  const [remarks, setRemarks] = useState("");
  const [rows, setRows] = useState([]);   // checklist rows for the selected room
  const [pars, setPars] = useState({});   // item_id -> expected/par qty for the room type
  const [currentBal, setCurrentBal] = useState({}); // item_id -> live in-room clean balance
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/rooms").then(({ data }) => setRooms(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedRoom = rooms.find(r => r.id === roomId);
  const roomType = selectedRoom ? (selectedRoom.room_type || roomTypeFor(selectedRoom.number)) : null;
  const setupPending = !!selectedRoom && !roomType;

  const loadRooms = () => api.get("/rooms").then(({ data }) => setRooms(data));
  const saveRoomType = async () => {
    if (!assignType) { toast.error("Choose a room type"); return; }
    try {
      await api.patch(`/rooms/${roomId}/room-type`, { room_type: assignType });
      toast.success(`Room ${selectedRoom.number} assigned to ${assignType}`);
      setAssignType(""); await loadRooms();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Build the checklist from saved room-template Item IDs, never from display-label matching.
  useEffect(() => {
    if (!roomId) {
      setRows([]);
      setPars({});
      return;
    }
    (async () => {
      try {
        const { data: roomPar } = await api.get(`/rooms/${roomId}/linen-par`);
        if (!roomPar.room_type_id) {
          setRows([]);
          setPars({});
          return;
        }
        const { data: template } = await api.get(
          `/room-types/${roomPar.room_type_id}/linen-template`,
        );
        const nextPars = { ...(roomPar.type_par || {}), ...(roomPar.overrides || {}) };
        setPars(nextPars);
        setRows(template.map(row => ({
          label: row.item_name || row.legacy_label,
          item_id: row.item_id || null,
          matched: !row.orphaned,
          launderable: row.launderable !== false,
          checked: false,
          action: "place_in_room",
          qty: 1,
          remark: "",
        })));
      } catch (error) {
        console.debug("Room template not available:", error?.message);
        setRows([]);
        setPars({});
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  const refreshRoomLiveData = useCallback(async () => {
    if (!roomId) {
      setCurrentBal({});
      return;
    }

    const [parResponse, balanceResponse] = await Promise.all([
      api.get(`/rooms/${roomId}/linen-par`),
      api.get(`/rooms/${roomId}/linen-balance`),
    ]);
    setPars({
      ...(parResponse.data.type_par || {}),
      ...(parResponse.data.overrides || {}),
    });
    setCurrentBal(balanceResponse.data || {});
  }, [roomId]);

  useEffect(() => {
    refreshRoomLiveData().catch(() => setCurrentBal({}));
  }, [refreshRoomLiveData]);

  useLiveRefresh(refreshRoomLiveData, Boolean(roomId));

  const upd = (idx, k, v) => setRows(rows.map((r, i) => (i === idx ? { ...r, [k]: v } : r)));
  const setAll = (checked) => setRows(rows.map(r => (r.matched ? { ...r, checked } : r)));
  const anyUnmatched = useMemo(() => rows.some(r => !r.matched), [rows]);

  const submit = async () => {
    if (!roomId) { toast.error("Select a room"); return; }
    const clean = rows.filter(r => r.checked && r.item_id && Number(r.qty) > 0);
    if (clean.length === 0) { toast.error("Tick at least one linen row to process"); return; }
    setBusy(true);
    const rowNotes = clean.filter(r => r.remark).map(r => `${r.label}: ${r.remark}`).join("; ");
    const combinedRemarks = [remarks, rowNotes].filter(Boolean).join(" | ");
    const requestId = globalThis.crypto?.randomUUID?.();
    if (!requestId) {
      toast.error("This browser cannot create a safe housekeeping request id.");
      setBusy(false);
      return;
    }
    try {
      await api.post("/housekeeping/entries", {
        operational_date: opDate, room_id: roomId, room_status: roomStatus || null,
        entries: clean.map(r => ({ item_id: r.item_id, action: r.action, qty: Number(r.qty) })),
        remarks: combinedRemarks,
        client_request_id: requestId,
      });
      await refreshRoomLiveData();
      toast.success("Housekeeping entry submitted");
      setRows(rows.map(r => ({ ...r, checked: false, qty: 1, remark: "", action: "place_in_room" })));
      setRemarks(""); setRoomStatus("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <h1 className="section-title" data-testid="page-title">{t("nav.housekeeping")}</h1>
      <div className="section-sub mb-4">{t("sub.housekeeping")}</div>

      <Card className="p-4 md:p-6 mb-24 md:mb-0">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <Label>{t("hk.op_date")}</Label>
            <Input type="date" value={opDate} onChange={e => setOpDate(e.target.value)} className="h-12 mt-1" data-testid="hk-date" />
          </div>
          <div>
            <Label>{t("col.room")}</Label>
            <Select value={roomId} onValueChange={setRoomId}>
              <SelectTrigger className="h-12 mt-1" data-testid="hk-room"><SelectValue placeholder={t("col.room")} /></SelectTrigger>
              <SelectContent>{rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.number} · {r.status}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("hk.room_status")}</Label>
            <Select value={roomStatus} onValueChange={setRoomStatus}>
              <SelectTrigger className="h-12 mt-1" data-testid="hk-room-status"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{ROOM_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {selectedRoom && (
          <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="hk-roomtype">
            <span className="text-sm text-slate-600">Room type:</span>
            {roomType
              ? <Badge className="bg-slate-900 text-white">{roomType}</Badge>
              : <Badge className="bg-amber-100 border text-amber-800" data-testid="hk-setup-pending">Setup Pending — Admin must assign a room type</Badge>}
          </div>
        )}

        {setupPending && (
          <div className="p-3 rounded border border-amber-300 bg-amber-50 text-sm text-amber-900" data-testid="hk-assign-block">
            No linen checklist is mapped to this room yet.
            {isAdmin ? (
              <div className="flex flex-wrap gap-2 items-center mt-2">
                <span className="text-amber-900">Assign a room type:</span>
                <Select value={assignType} onValueChange={setAssignType}>
                  <SelectTrigger className="h-9 w-48 bg-white" data-testid="hk-assign-type"><SelectValue placeholder="Select room type" /></SelectTrigger>
                  <SelectContent>{ROOM_TYPES.map(rt => <SelectItem key={rt} value={rt}>{rt}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" className="bg-slate-900 text-white hover:bg-slate-800" onClick={saveRoomType} data-testid="hk-assign-save">Save</Button>
              </div>
            ) : <span> An Admin can assign its room type before daily entries are recorded.</span>}
          </div>
        )}

        {!!rows.length && (
          <>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-700">Linen checklist</div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setAll(true)} data-testid="hk-select-all">Select All</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setAll(false)} data-testid="hk-clear-all">Clear All</Button>
              </div>
            </div>
            {anyUnmatched && (
              <div className="mb-2 text-xs text-red-700">Some checklist items are not matched to Master Linen Stock and need Admin mapping — they are disabled.</div>
            )}
            <div className="overflow-x-auto">
              <table className="vr-table" data-testid="hk-checklist">
                <thead><tr>
                  <th className="w-8"></th><th>Linen item</th><th className="num">Current</th><th className="num">Expected</th>
                  <th>Action</th><th className="num">Qty</th><th>Remarks</th>
                </tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.label} className={!r.matched ? "opacity-60" : ""}>
                      <td>
                        <input type="checkbox" disabled={!r.matched} checked={r.checked}
                          onChange={e => upd(i, "checked", e.target.checked)} data-testid={`hk-check-${i}`} />
                      </td>
                      <td>{r.label}{!r.matched && <span className="text-xs text-red-600"> · needs mapping</span>}</td>
                      <td className="num">{r.item_id ? (currentBal[r.item_id] || 0) : <span className="text-slate-400">—</span>}</td>
                      <td className="num">
                        {r.item_id && pars[r.item_id] != null ? (() => {
                          const exp = Number(pars[r.item_id]);
                          const cur = Number(currentBal[r.item_id] || 0);
                          const diff = cur - exp;
                          return (
                            <div className="flex items-center justify-end gap-2">
                              <span>{exp}</span>
                              {diff === 0
                                ? <Badge className="bg-emerald-100 border text-emerald-700 text-[10px]" data-testid={`hk-variance-${i}`}>OK</Badge>
                                : diff < 0
                                  ? <Badge className="bg-red-100 border text-red-700 text-[10px]" data-testid={`hk-variance-${i}`}>Short {Math.abs(diff)}</Badge>
                                  : <Badge className="bg-sky-100 border text-sky-700 text-[10px]" data-testid={`hk-variance-${i}`}>Over {diff}</Badge>}
                            </div>
                          );
                        })() : <span className="text-slate-400 text-xs">Not Configured</span>}
                      </td>
                      <td>
                        <Select value={r.action} onValueChange={v => upd(i, "action", v)} disabled={!r.checked}>
                          <SelectTrigger className="h-9 w-40" data-testid={`hk-action-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{ACTIONS_KEYS.filter(a => a.value !== "found_dirty" || r.launderable).map(a => <SelectItem key={a.value} value={a.value}>{a.label || t(a.key)}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="num">
                        <Input type="number" min="1" value={r.qty} disabled={!r.checked}
                          onChange={e => upd(i, "qty", e.target.value)} className="h-9 w-20 ml-auto text-right" data-testid={`hk-qty-${i}`} />
                      </td>
                      <td>
                        <Input value={r.remark} disabled={!r.checked} onChange={e => upd(i, "remark", e.target.value)} className="h-9" data-testid={`hk-remark-${i}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-xs text-slate-400 mt-1">Expected/par quantities can be configured by room type in Admin Settings. Only ticked rows are processed on submit.</div>
          </>
        )}

        <div className="mt-4">
          <Label>{t("col.remarks")}</Label>
          <Textarea value={remarks} onChange={e => setRemarks(e.target.value)} className="mt-1" data-testid="hk-remarks" />
        </div>
      </Card>

      <div className="sticky-actions md:static md:mt-4 md:bg-transparent md:border-0 md:p-0 no-print">
        <Button className="w-full md:w-auto h-12 bg-slate-900 hover:bg-slate-800 text-white px-8" onClick={submit} disabled={busy || !rows.length} data-testid="hk-submit">
          {busy ? t("auth.signing_in") : t("hk.submit")}
        </Button>
      </div>
    </Layout>
  );
}
